import React, { useEffect, useState } from 'react';
import { Row, Col, Tooltip } from 'antd';
import { Link, useParams } from 'react-router-dom';
import {
    UilArrowDown,
    UilArrowUp,
    UilAdjustCircle,
    UilUsersAlt,
    UilClock,
    UilLink,
} from '@iconscout/react-unicons';
import { PageHeader } from '../../components/page-headers/page-headers';
import { Main } from '../styled';
import { Cards } from '../../components/cards/frame/cards-frame';
import { ProjectDetailsWrapper, ProjectListAssignees } from '../project/style';
import { useSelector, useDispatch } from 'react-redux';
import { singleTaskDataRead } from '../../redux/tasks/actionCreator';
import Heading from '../../components/heading/heading';
import { formatDate } from '../../components/formatDate/FormatDate';

function TaskDetails() {
    const params = useParams();
    const taskId = params.id;
    const dispatch = useDispatch();

    const [dataSource, setDataSource] = useState([]);

    const { SingleTaskRes } = useSelector((state) => ({
        SingleTaskRes: state.SingleTaskRes.data,
    }));

    useEffect(() => {
        if (dispatch) {
            dispatch(singleTaskDataRead(parseInt(taskId)));
        }
    }, [dispatch, taskId]);

    function daysBetween(createdDate) {
        const created = new Date(createdDate);
        const today = new Date();
        created.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        const diffTime = today - created;
        return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    }

    useEffect(() => {
        if (SingleTaskRes && Object.keys(SingleTaskRes).length > 0) {
            const filteredData = SingleTaskRes?.map((item) => ({
                deadline: item?.deadline !== null ? item?.deadline : 'NA',
                status: item?.status,
                description: item?.description,
                title: item?.title,
                id: item?.id,
                createdon: item?.createdon,
                priority: item?.priority,
                persons: item?.person?.map((person) => ({
                    name: person?.name,
                    imagename: person?.imagename,
                    username: person?.usermaster?.username,
                    id: person?.id,
                })),
                project: item?.project || [],
                product: item?.product || [],
            }));
            setDataSource(filteredData);
        }
    }, [SingleTaskRes]);

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'HIGH':
                return <UilArrowUp />;
            case 'MEDIUM':
                return <UilAdjustCircle />;
            case 'LOW':
                return <UilArrowDown />;
            default:
                return null;
        }
    };

    const task = dataSource && dataSource[0];

    return (
        <ProjectDetailsWrapper>
            <PageHeader
                className="ninjadash-page-header-main"
                ghost
                title={`Task Details of ${task?.title}`}
            />
            <Main>
                <Row gutter={25}>
                    <Col xxl={6} xl={8} xs={24}>
                        <div className="project-progress">
                            <h3>{task?.status}</h3>
                        </div>
                        <Cards headless>
                            <div className="state-single">
                                <div className="color-warning">
                                    <Link>{getPriorityIcon(task?.priority)}</Link>
                                </div>
                                <div>
                                    <Heading as="h4">{task?.priority}</Heading>
                                    <p>Priority</p>
                                </div>
                            </div>

                            <div className="state-single">
                                <div className="color-secondary">
                                    <Link>
                                        <UilUsersAlt />
                                    </Link>
                                </div>
                                <div>
                                    <ProjectListAssignees>
                                        {task?.persons?.length > 0 ? (
                                            <ul>
                                                {task?.persons.map((person, index) => {
                                                    let imageSrc;
                                                    try {
                                                        imageSrc = `${process.env.REACT_APP_API_ENDPOINT}/public/profile/${person?.imagename}`;
                                                    } catch {
                                                        imageSrc = null;
                                                    }
                                                    return (
                                                        <Tooltip title={person?.name} key={person?.id || index}>
                                                            <li>
                                                                {imageSrc ? (
                                                                    <div className="cursor-style">
                                                                        <img
                                                                            className="img-border-radius img-dimensions"
                                                                            src={imageSrc}
                                                                            alt=""
                                                                            onError={(e) => {
                                                                                e.target.style.display = 'none';
                                                                                e.target.parentNode.appendChild(
                                                                                    document.createElement('div'),
                                                                                ).innerHTML = `<span class="ant-avatar ant-avatar-circle">
                                          ${person.name[0].toUpperCase()}
                                        </span>`;
                                                                            }}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <span className="ant-avatar ant-avatar-circle">
                                                                        {person.name[0].toUpperCase()}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        </Tooltip>
                                                    );
                                                })}
                                            </ul>
                                        ) : (
                                            <h4>Not Assigned Yet</h4>
                                        )}
                                    </ProjectListAssignees>
                                    <p>Assigned To</p>
                                </div>
                            </div>

                            <div className="state-single">
                                <div className="color-primary">
                                    <Link>
                                        <UilClock />
                                    </Link>
                                </div>
                                <div>
                                    <Heading as="h5">{daysBetween(task?.createdon)}</Heading>
                                    <p>Days Completed</p>
                                </div>
                            </div>
                        </Cards>
                    </Col>

                    <Col xxl={12} xl={16} xs={24}>
                        <Cards title={`About ${task?.title}`}>
                            <div className="about-content">
                                <h4>Description</h4>
                                <p>{task?.description}</p>
                            </div>
                            <div className="about-project">
                                <div>
                                    <span>Start Date</span>
                                    <p className="color-primary">
                                        {task?.createdon ? formatDate(task?.createdon) : 'NA'}
                                    </p>
                                </div>
                                <div>
                                    <span>Deadline</span>
                                    <p className="color-danger">
                                        {task?.deadline !== 'NA'
                                            ? formatDate(task?.deadline)
                                            : 'NA'}
                                    </p>
                                </div>
                            </div>
                        </Cards>

                        <Cards title="Project & Product Information">
                            <Row gutter={[25, 25]}>
                                <Col xs={24} md={12}>
                                    <div className="about-content">
                                        <h2>Project</h2>
                                        {task?.project?.length > 0 ? (
                                            task.project.map((proj) => (
                                                <div key={proj.id} style={{ marginBottom: '15px' }}>
                                                    <p>
                                                        <b>Project Name:</b> {proj.project || 'NA'}
                                                    </p>
                                                    <p>
                                                        <b>Created By:</b> {proj.createdBy || 'NA'}
                                                    </p>
                                                    <p>
                                                        <b>Created On:</b>{' '}
                                                        {proj.createdon ? formatDate(proj.createdon) : 'NA'}
                                                    </p>
                                                    <p>
                                                        <b>Updated On:</b>{' '}
                                                        {proj.updatedon ? formatDate(proj.updatedon) : 'NA'}
                                                    </p>

                                                    {proj.listOfTask && proj.listOfTask.length > 0 && (
                                                        <div style={{ marginTop: '10px' }}>
                                                            <b>List of Tasks:</b>
                                                            <ul style={{ paddingLeft: '20px' }}>
                                                                {proj.listOfTask.map((taskName, index) => (
                                                                    <li key={index}>{taskName}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <p>NA</p>
                                        )}
                                    </div>
                                </Col>

                                <Col xs={24} md={12}>
                                    <div className="about-content">
                                        <h2>Product</h2>
                                        {task?.product?.length > 0 ? (
                                            task.product.map((prod) => (
                                                <div key={prod.id} style={{ marginBottom: '10px' }}>
                                                    <p>
                                                        <strong>{prod.productname}</strong>
                                                    </p>
                                                    <ul style={{ paddingLeft: '20px', marginBottom: '5px' }}>
                                                        {prod.devLink && (
                                                            <li>
                                                                <UilLink />{' '}
                                                                <a href={prod.devLink} target="_blank" rel="noreferrer">
                                                                    Dev Link
                                                                </a>
                                                            </li>
                                                        )}
                                                        {prod.userLink && (
                                                            <li>
                                                                <UilLink />{' '}
                                                                <a href={prod.userLink} target="_blank" rel="noreferrer">
                                                                    User Link
                                                                </a>
                                                            </li>
                                                        )}
                                                        {prod.gitLink && (
                                                            <li>
                                                                <UilLink />{' '}
                                                                <a href={prod.gitLink} target="_blank" rel="noreferrer">
                                                                    Git Repo
                                                                </a>
                                                            </li>
                                                        )}
                                                        {prod.requrirementDoc && (
                                                            <li>
                                                                <UilLink />{' '}
                                                                <a href={prod.requrirementDoc} target="_blank" rel="noreferrer">
                                                                    Requirement Doc
                                                                </a>
                                                            </li>
                                                        )}
                                                        {prod.designDoc && (
                                                            <li>
                                                                <UilLink />{' '}
                                                                <a href={prod.designDoc} target="_blank" rel="noreferrer">
                                                                    Design Doc
                                                                </a>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            ))
                                        ) : (
                                            <p>NA</p>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Cards>

                    </Col>
                </Row>
            </Main>
        </ProjectDetailsWrapper>
    );
}

export default TaskDetails;
